import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { LoginCallback, AuthGuard } from "@vmlmap/shared-ui/auth";

import { AppShell } from "@/layouts/AppShell";
import { ReportsListView } from "@/views/ReportsListView";
import { ReportDetailView } from "@/views/ReportDetailView";
import { InstantAnalysisView } from "@/views/InstantAnalysisView";
import { LoadFromUrlView } from "@/views/LoadFromUrlView";
import { UnauthorizedView } from "@/views/UnauthorizedView";
import { UserManagementView } from "@/views/UserManagementView";
import { getCurrentRole } from "@/contexts/RoleContext";

const rootRoute = createRootRoute({
  component: Outlet
});

const loginCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login/callback",
  component: LoginCallback
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  component: AuthGuard
});

const shellRoute = createRoute({
  getParentRoute: () => authRoute,
  id: "_shell",
  component: AppShell
});

const unauthorizedRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/unauthorized",
  component: UnauthorizedView
});

// Root redirects to reports view
const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  beforeLoad: () => {
    const { actualRole, isAuthenticated } = getCurrentRole();
    // Only redirect to unauthorized if authenticated but no role assigned
    if (isAuthenticated && actualRole === 'NONE') {
      throw redirect({ to: "/unauthorized" });
    }
    throw redirect({ to: "/reports" });
  }
});

const instantAnalysisRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/instant-analysis",
  beforeLoad: () => {
    const { actualRole, isAuthenticated } = getCurrentRole();
    if (isAuthenticated && actualRole === 'NONE') {
      throw redirect({ to: "/unauthorized" });
    }
  },
  component: InstantAnalysisView
});

const loadFromUrlRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/load-from-url",
  beforeLoad: () => {
    const { actualRole, isAuthenticated } = getCurrentRole();
    if (isAuthenticated && actualRole === 'NONE') {
      throw redirect({ to: "/unauthorized" });
    }
  },
  component: LoadFromUrlView
});

// Reports list (adapts to user role via component)
const reportsListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports",
  beforeLoad: () => {
    const { actualRole, isAuthenticated } = getCurrentRole();
    if (isAuthenticated && actualRole === 'NONE') {
      throw redirect({ to: "/unauthorized" });
    }
  },
  component: ReportsListView
});

// Report detail (adapts to user role via component)
const reportDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/reports/$reportId",
  beforeLoad: () => {
    const { actualRole, isAuthenticated } = getCurrentRole();
    if (isAuthenticated && actualRole === 'NONE') {
      throw redirect({ to: "/unauthorized" });
    }
  },
  component: ReportDetailView
});

// User management (admin only)
const userManagementRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/users",
  beforeLoad: () => {
    const { actualRole, isAuthenticated } = getCurrentRole();
    if (!isAuthenticated || actualRole !== 'ADMIN') {
      throw redirect({ to: "/unauthorized" });
    }
  },
  component: UserManagementView
});

const routeTree = rootRoute.addChildren([
  loginCallbackRoute,
  authRoute.addChildren([
    unauthorizedRoute,
    shellRoute.addChildren([
      indexRoute,
      instantAnalysisRoute,
      loadFromUrlRoute,
      userManagementRoute,
      reportsListRoute,
      reportDetailRoute
    ])
  ])
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export default router;
